import { NavLink } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAgentStatus } from '../../hooks/useAgentStatus';
import { useAppConfigs } from '../../hooks/useAppConfigs';
import type { AppName, AppRole } from '../../types';
import styles from './Sidebar.module.css';

// Orden canónico para la sidebar (se filtra por status en el render)
const APP_ORDER: AppName[] = ['broota', 'xbroota', 'nalca', 'dully', 'team-ticket', 'general'];

interface SidebarProps {
  isOpen?:       boolean;
  isCollapsed?:  boolean;
  onClose?:      () => void;
  onToggle?:     () => void;
}

export function Sidebar({ isOpen = false, isCollapsed = false, onClose, onToggle }: SidebarProps) {
  const { teamUser, isSysadmin } = useAuth();
  const { status, checking, recheck } = useAgentStatus();
  // Carga configs para aplicar CSS vars y filtrar apps deshabilitadas
  const { apps } = useAppConfigs();
  const visibleApps = APP_ORDER
    .map(id => apps.find(a => a.id === id))
    .filter((a): a is NonNullable<typeof a> => {
      if (!a || a.status === 'disabled') return false;
      if (isSysadmin || a.id === 'general') return true;
      const role: AppRole | undefined = teamUser?.appRoles?.[a.id as AppName];
      return role != null && role !== 'none';
    });

  const appCssVar = (id: string) => `var(--${id})`;

  const semColor = {
    ok:       'var(--sem-ok)',
    degraded: 'var(--sem-warn)',
    error:    'var(--sem-err)',
  }[status.status];

  const semLabel = {
    ok:       'Operacional',
    degraded: 'Latencia alta',
    error:    'Sin conexión',
  }[status.status];

  const initials = teamUser?.displayName
    ?.split(' ')
    .map(n => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() ?? '??';

  return (
    <aside className={`${styles.sidebar} ${isOpen ? styles.sidebarOpen : ''} ${isCollapsed ? styles.sidebarCollapsed : ''}`}>
      {/* Mobile close button */}
      <button
        className={styles.closeBtn}
        onClick={onClose}
        aria-label="Cerrar menú">
        <IconClose />
      </button>

      {/* Logo */}
      <div className={styles.logo}>
        <div className={styles.logoMark}>O</div>
        {!isCollapsed && (
          <div className={styles.logoText_wrap}>
            <div className={styles.logoText}>Oski</div>
            <div className={styles.logoSub}>AI WORKSPACE</div>
          </div>
        )}
        <button
          className={styles.desktopToggle}
          onClick={onToggle}
          aria-label={isCollapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}>
          {isCollapsed ? <IconChevronRight /> : <IconChevronLeft />}
        </button>
      </div>

      {/* Nav */}
      <nav className={styles.nav}>
        {!isCollapsed && <div className={styles.navLabel}>General</div>}

        <NavLink to="/" end className={({ isActive }) =>
          `${styles.navItem} ${isActive ? styles.active : ''}`}
          title={isCollapsed ? 'Oski Chat' : undefined}>
          <IconGrid />
          {!isCollapsed && 'Oski Chat'}
        </NavLink>

        <NavLink to="/tickets" className={({ isActive }) =>
          `${styles.navItem} ${isActive ? styles.active : ''}`}
          title={isCollapsed ? 'Tickets' : undefined}>
          <IconTicket />
          {!isCollapsed && 'Tickets'}
        </NavLink>

        <div className={styles.navSection}>
          {!isCollapsed && <div className={styles.navLabel}>Por aplicación</div>}
          {visibleApps.map(app => {
            const label = app.id === 'team-ticket' ? 'Oskiapp'
              : app.id === 'general' ? 'General'
              : app.label;
            return (
              <NavLink
                key={app.id}
                to={app.id === 'general' ? '/tickets' : `/tickets?app=${app.id}`}
                className={({ isActive }) =>
                  `${styles.navItem} ${isActive ? styles.active : ''}`}
                title={isCollapsed ? label : undefined}>
                <span className={styles.appDot} style={{ background: appCssVar(app.id) }} />
                {!isCollapsed && label}
              </NavLink>
            );
          })}
        </div>

        {isSysadmin && (
          <div className={styles.navSection}>
            {!isCollapsed && <div className={styles.navLabel}>Admin</div>}
            <NavLink to="/admin" end className={({ isActive }) =>
              `${styles.navItem} ${isActive ? styles.active : ''}`}
              title={isCollapsed ? 'Equipo' : undefined}>
              <IconTeam />
              {!isCollapsed && 'Equipo'}
            </NavLink>
            <NavLink to="/admin/config" className={({ isActive }) =>
              `${styles.navItem} ${isActive ? styles.active : ''}`}
              title={isCollapsed ? 'Agentes' : undefined}>
              <IconConfig />
              {!isCollapsed && 'Agentes'}
            </NavLink>
            <NavLink to="/admin/apps" className={({ isActive }) =>
              `${styles.navItem} ${isActive ? styles.active : ''}`}
              title={isCollapsed ? 'Apps' : undefined}>
              <IconApps />
              {!isCollapsed && 'Apps'}
            </NavLink>
            <NavLink to="/sysadmin/audit" className={({ isActive }) =>
              `${styles.navItem} ${isActive ? styles.active : ''}`}
              title={isCollapsed ? 'Auditoría' : undefined}>
              <IconAudit />
              {!isCollapsed && 'Auditoría'}
            </NavLink>
          </div>
        )}
      </nav>

      {/* Footer */}
      <div className={styles.footer}>
        {/* Semáforo API */}
        <button
          className={styles.apiCard}
          onClick={recheck}
          title={isCollapsed ? `OSKI · API — ${checking ? 'Verificando...' : semLabel}` : 'Click para re-verificar'}>
          <div className={styles.semaforo}>
            <div className={styles.semLight}
              style={{ background: status.status === 'error'    ? semColor : 'var(--surface3)' }} />
            <div className={styles.semLight}
              style={{ background: status.status === 'degraded' ? semColor : 'var(--surface3)' }} />
            <div className={styles.semLight}
              style={{
                background: status.status === 'ok' ? semColor : 'var(--surface3)',
                animation: status.status === 'ok' ? 'pulse-ok 2.5s ease-in-out infinite' : 'none',
              }} />
          </div>
          {!isCollapsed && (
            <div className={styles.apiInfo}>
              <div className={styles.apiLabel}>OSKI · API</div>
              <div className={styles.apiText} style={{ color: semColor }}>
                {checking ? 'Verificando...' : semLabel}
              </div>
              <div className={styles.apiPing}>
                {status.latencyMs ? `${status.latencyMs}ms` : '—'}
              </div>
            </div>
          )}
          {!isCollapsed && <IconRefresh className={checking ? styles.spinning : ''} />}
        </button>

        {/* Servicios individuales */}
        {!isCollapsed && status.services && (
          <div className={styles.servicesList}>
            {([
              { key: 'anthropic',   label: 'Anthropic'   },
              { key: 'notion',      label: 'Notion'      },
              { key: 'github_pame', label: 'GitHub Pame' },
              { key: 'github_neo',  label: 'GitHub Neo'  },
            ] as const).map(({ key, label }) => {
              const svcStatus = status.services![key];
              const color = svcStatus === 'ok' ? 'var(--sem-ok)' : 'var(--sem-err)';
              return (
                <div key={key} className={styles.serviceRow}>
                  <span className={styles.serviceDot} style={{ background: color }} />
                  <span className={styles.serviceLabel}>{label}</span>
                  <span className={styles.serviceStatus} style={{ color }}>
                    {svcStatus === 'ok' ? 'OK' : 'Error'}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* User */}
        <div className={styles.userCard} title={isCollapsed ? `${teamUser?.displayName} · ${teamUser?.email}` : undefined}>
          <div className={styles.avatar}>{initials}</div>
          {!isCollapsed && (
            <div className={styles.userInfo}>
              <div className={styles.userName}>{teamUser?.displayName}</div>
              <div className={styles.userEmail}>{teamUser?.email}</div>
            </div>
          )}
          {!isCollapsed && isSysadmin && <span className={styles.adminBadge}>SYS</span>}
        </div>
      </div>
    </aside>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────
function IconClose() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
      <path d="M2 2l12 12M14 2L2 14"/>
    </svg>
  );
}
function IconGrid() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="1" y="1" width="6" height="6" rx="1.5"/>
      <rect x="9" y="1" width="6" height="6" rx="1.5"/>
      <rect x="1" y="9" width="6" height="6" rx="1.5"/>
      <rect x="9" y="9" width="6" height="6" rx="1.5"/>
    </svg>
  );
}
function IconTicket() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M2 4h12v8H2z" rx="2"/>
      <path d="M5 4v8M11 4v8"/>
    </svg>
  );
}
function IconTeam() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="6" cy="5" r="2.5"/>
      <path d="M1 13c0-2.76 2.24-5 5-5"/>
      <circle cx="12" cy="10" r="2.5"/>
      <path d="M9.5 12.5h5M12 10v5"/>
    </svg>
  );
}
function IconConfig() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="8" cy="8" r="2"/>
      <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.05 3.05l1.41 1.41M11.54 11.54l1.41 1.41M3.05 12.95l1.41-1.41M11.54 4.46l1.41-1.41"/>
    </svg>
  );
}
function IconChevronLeft() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10 3L5 8l5 5"/>
    </svg>
  );
}
function IconChevronRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 3l5 5-5 5"/>
    </svg>
  );
}
function IconApps() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="4.5" cy="4.5" r="2"/>
      <circle cx="11.5" cy="4.5" r="2"/>
      <circle cx="4.5" cy="11.5" r="2"/>
      <circle cx="11.5" cy="11.5" r="2"/>
    </svg>
  );
}
function IconAudit() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <path d="M2 13V3M2 13h12M5 10V7M8 10V5M11 10V8"/>
    </svg>
  );
}
function IconRefresh({ className = '' }: { className?: string }) {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none"
      stroke="currentColor" strokeWidth="1.5"
      className={className}
      style={{ flexShrink: 0, color: 'var(--muted2)' }}>
      <path d="M13.5 8A5.5 5.5 0 112.5 5M2.5 2v3h3"/>
    </svg>
  );
}
